// src/pages/ledger/PatientLedger.js
import React, { useState, useEffect, Fragment } from "react";
import { Card, CardBody, Col, Table, Input, Button, Label } from "reactstrap";
import { Form, Modal, Row } from "react-bootstrap";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import { Eye, DollarSign, RefreshCcw, PlusCircle, X } from "react-feather";
import errorProfile from "../.././assets/images/blank_profile.png";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { toast } from "react-toastify";
import MyPagination from "../pagination";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { addLedgerPayment, getPatientLedger } from "./PatientLedgerService";
import Swal from "sweetalert2";
import { InputGroup } from "react-bootstrap";
import SearchPatientModel from "./SearchPatientModal";
import { FaLocationDot } from "react-icons/fa6";
import { TfiEmail } from "react-icons/tfi";
import { FiPhoneCall } from "react-icons/fi";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { ledgerPageAtom, ledgerPageSizeAtom } from "./ledgerAtoms";
import S3ImageLoader from "../../S3ImageLoader";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";
import { useCurrency } from "../../_helper/CurrencyContext";
import {
  canShowPatientAccounts,
  isHospitalConnected as isPharmacyHospitalConnected,
  pharmacyCollectsPayment as getPharmacyCollectsPayment,
} from "../../utils/pharmacyConnection";
import CurrencyAmountInput from "../PharmacySales/NewSales/components/CurrencyAmountInput";
import { convertCurrency as convertCurrencyEngine, toMoneyNumber } from "../../utils/financeEngine";

const imageBaseURL = process.env.REACT_APP_BASE_URL;

const PatientLedger = () => {
  const { t } = useTranslation();
  const { setLoading } = useLoader();
  const { pharmacyPOS } = usePharmacy();
  const { getCurrencySymbol, currency, convertCurrency } = useCurrency();

  const isHospitalConnected = isPharmacyHospitalConnected(pharmacyPOS);
  const pharmacyCollectsPayment = getPharmacyCollectsPayment(pharmacyPOS);
  const showPatientAccounts = canShowPatientAccounts(pharmacyPOS);
  const history = useNavigate();

  const formatAmount = (cur, amount) => {
    if (!cur) return Number(amount || 0).toFixed(2);
    const prefix = getCurrencySymbol(cur, "prefix");
    const suffix = getCurrencySymbol(cur, "suffix");

    return prefix + Number(amount || 0).toFixed(2) + (cur === 'ZiG' ? suffix : '');
  };

  const generateTooltip = (currencies, amountType) => {
    return Object.entries(currencies)
      .map(([cur, data]) => `${cur}: ${formatAmount(cur, data[amountType])}`)
      .join("\n");
  };

  const [ledger, setLedger] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useAtom(ledgerPageAtom);
  const [size, setSize] = useAtom(ledgerPageSizeAtom);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isPatientModal, setIsPatientModal] = useState(false);

  const [viewTooltipOpen, setViewTooltipOpen] = useState(false);
  const [settlementTooltipOpen, setSettlementTooltipOpen] = useState(false);
  const [returnTooltipOpen, setReturnTooltipOpen] = useState(false);
  const [saleTooltipOpen, setSaleTooltipOpen] = useState(false);
  const [actionTooltips, setActionTooltips] = useState({});

  const toggleViewTooltip = () => setViewTooltipOpen(!viewTooltipOpen);
  const toggleSettlementTooltip = () =>
    setSettlementTooltipOpen(!settlementTooltipOpen);
  const toggleReturnTooltip = () => setReturnTooltipOpen(!returnTooltipOpen);
  const toggleSaleTooltip = () => setSaleTooltipOpen(!saleTooltipOpen);
  const toggleActionTooltip = (id) => {
    setActionTooltips((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openPatientAccounts = (txn) => {
    history(`${process.env.PUBLIC_URL}/patient-accounts`, {
      state: {
        patientId: txn.patientId,
        patientName: txn.patientName,
        activeTab: "partial_paid",
      },
    });
  };

  // Settlement Modal
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [settlementData, setSettlementData] = useState({
    patientId: "",
    patientName: "",
    currency: "",
    paymentType: "cash",
    remainingPrimary: 0,
    remainingSecondary: 0,
    amount: "",
    primaryCurrency: "",
    secondaryCurrency: "",
    baseCurrency: "",
    rates: {},
    isSettle: false,
    customerType: "inpatient",
    isHospitalConnected: false,
  });

  const [errors, setErrors] = useState({});
  const disableScrollProps = useDisableScrollOnFocus();

  useEffect(() => {
    fetchLedger(selectedPatient);
  }, [page, size, statusFilter]);

  const fetchLedger = async (patientArg = null) => {
    try {
      setLoading(true);
      const body = {
        clinicId: pharmacyPOS?.hospital_id,
        patientId: patientArg?._id || "",
        isPending: statusFilter,
        page,
        limit: size,
      };

      const response = await getPatientLedger(body);
      const rawPayments = response.data.data.payments || [];
      const processedLedger = rawPayments.map((payment) => {
        const baseCurrency = payment.baseCurrency;
        const rates = { ...payment.currencyRates };
        const primaryCurrency = payment.currencyType;
        const allCurrencies = Object.keys(rates);
        const otherCurrencies = allCurrencies.filter((c) => c !== primaryCurrency);
        const secondaryCurrency = otherCurrencies[0] || null;
        const hasMoreCurrencies = otherCurrencies.length > 1;

        let totalBase;
        if (payment.salesType === "refund") {
          totalBase = payment.convertedToBase;
        } else {
          totalBase = payment.convertedToBase + (payment.balances[baseCurrency] || 0);
        }

        const currencies = {};
        allCurrencies.forEach((cur) => {
          const rate = rates[cur];
          const paidCur = payment.convertedToBase / rate;
          const balanceCur = payment.balances[cur] || 0;
          const totalCur = totalBase / rate;
          currencies[cur] = { paid: paidCur, balance: balanceCur, total: totalCur };
        });

        return {
          ...payment,
          primaryCurrency,
          secondaryCurrency,
          hasMoreCurrencies,
          rates,
          currencies,
          totalPrimary: currencies[primaryCurrency]?.total || 0,
          paidPrimary: currencies[primaryCurrency]?.paid || 0,
          balancePrimary: currencies[primaryCurrency]?.balance || 0,
          ...(secondaryCurrency && {
            totalSecondary: currencies[secondaryCurrency].total,
            paidSecondary: currencies[secondaryCurrency].paid,
            balanceSecondary: currencies[secondaryCurrency].balance,
          }),
        };
      });
      setLedger(processedLedger);
      const totalCount = response.data.data.total || 0;
      setTotalItems(Math.ceil(totalCount / size));
    } catch (err) {
      console.error("Ledger fetch error:", err);
      setLedger([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (patient) => {
    setPage(1);
    fetchLedger(patient);
  };

  const handleClear = () => {
    setSearchValue("");
    setStatusFilter("");
    setPage(1);
    setSearchQuery("");
    setSelectedPatient(null);
    fetchLedger(null);
  };

  const handlePageChange = (num) => setPage(num);

  const handlePageSizeChange = (e) => {
    setSize(parseInt(e.target.value));
    setPage(1);
  };

  const toBaseCurrency = (amount, fromCurrency, baseCurrency, rates) => {
    if (fromCurrency === baseCurrency) {
      return toMoneyNumber(amount);
    }
    return toMoneyNumber(convertCurrencyEngine(amount, fromCurrency, baseCurrency, rates));
  };

  const remainingInBase = toBaseCurrency(
    settlementData.remainingPrimary,
    settlementData.primaryCurrency,
    settlementData.baseCurrency,
    settlementData.rates
  );

  const paidBaseAmount = toBaseCurrency(
    settlementData.amount,
    settlementData.currency,
    settlementData.baseCurrency,
    settlementData.rates
  );

  // Open Settlement Modal
  const openSettlementModal = (txn) => {
    const paidInCur = txn.currency;
    setSettlementData({
      patientId: txn.patientId,
      patientName: txn.patientName,
      customerType: txn.customerType,
      isHospitalConnected: txn.isHospitalConnected,
      currency: paidInCur,
      paymentType: "cash",

      // Store per-currency balances for UI use
      balances: txn.balances,
      remainingAmounts: txn.balances,

      // Compute remaining values for primary + secondary
      remainingPrimary: txn.balances[paidInCur],
      remainingSecondary: txn.secondaryCurrency ? txn.balanceSecondary : 0,

      amount: txn.balances[paidInCur],
      primaryCurrency: paidInCur,
      secondaryCurrency: txn.secondaryCurrency,
      baseCurrency: txn.baseCurrency,
      rates: txn.currencyRates,
      isSettle: false,
    });
    setShowSettlementModal(true);
  };

  const handleSettlementSave = async () => {
    let newErrors = {};
    if (!settlementData.paymentType)
      newErrors.paymentType = "Payment type is required";
    if (!settlementData.amount) newErrors.amount = "Amount is required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Full/partial settlement (base currency), for future API fields:
    // isPaid = isEffectivelyPaidInFull(remainingInBase, paidBaseAmount)
    // isPartial = paidBaseAmount > 0 && !isPaid

    const body = {
      clinicId: pharmacyPOS.hospital_id,
      patientId: settlementData.patientId,
      pharmacyId: pharmacyPOS._id,
      patientName: settlementData.patientName,

      payingAmount: settlementData.amount,
      currencyType: settlementData.currency,

      paymentType: settlementData.paymentType,

      isConversion: settlementData.currency !== settlementData.baseCurrency,
      isHospitalConnected: settlementData.isHospitalConnected,
      customerType: settlementData.customerType,
      // zigConversionValue: settlementData.rates[settlementData.currency] || 1,
      // isPartialPaid,
      // isPaid,
      isSettled: settlementData.isSettle,
    };
    // console.log('body', body);
    // console.log('settlementData', settlementData);

    try {
      setLoading(true);
      const response = await addLedgerPayment(body);
      if (response.data.status === "success") {
        toast.success(t(response.data.message));
        setShowSettlementModal(false);
        fetchLedger(selectedPatient);
      }
    } catch (err) {
      console.error("Settlement error:", err);
    } finally {
      setLoading(false);
    }
  };

  // const handleCurrencyChange = (newCurrency) => {
  //   setCurrency(newCurrency);
  //   const remaining = newCurrency === settlementData.primaryCurrency
  //     ? settlementData.remainingPrimary
  //     : settlementData.remainingSecondary;
  //   setSettlementData({
  //     ...settlementData,
  //     currency: newCurrency,
  //     amount: remaining,
  //   });
  // };

  const handleSettlePayment = (e) => {
    e.preventDefault();

    const isCurrentlySettle = settlementData.isSettle;
    const actionText = isCurrentlySettle
      ? t("Do you want to un-settle this payment?")
      : t("Do you want to settle this payment?");

    const confirmText = isCurrentlySettle
      ? t("Yes, Unsettle it!")
      : t("Yes, Settle it!");

    Swal.fire({
      title: t("Are you sure?"),
      text: actionText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: t("No, Cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        setSettlementData({ ...settlementData, isSettle: !isCurrentlySettle });
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString(undefined, { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleSearchClick = () => {
    if (searchQuery.trim().length >= 3) {
      setIsPatientModal(true);
    }
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchClick();
    }
  };

  const calculateAge = (birthDate) => {
    const birthDateMoment = moment(birthDate);
    const currentDate = moment();
    const age = currentDate.diff(birthDateMoment, "years");
    return age;
  };

  const getTransactionTypeLabel = (salesType) => {
    return t(`${salesType.charAt(0).toUpperCase() + salesType.slice(1)}`);
  };

  const getPatientImageUrl = (profilePicture) => {
    if (!profilePicture) return errorProfile;

    if (profilePicture.startsWith("http")) {
      return profilePicture;
    }

    return `${imageBaseURL.replace(/\/$/, "")}/${profilePicture.replace(/^\//, "")}`;
  };

  const getStatusBadge = (payment) => {
    const hasOutstanding =
      payment.balancePrimary > 0 ||
      (payment.secondaryCurrency && payment.balanceSecondary > 0) ||
      (payment.hasMoreCurrencies &&
        Object.values(payment.currencies)
          .slice(2)
          .some((c) => c.balance > 0));
    if (payment.isSettled) {
      return <span className="badge bg-success">{t("Settled")}</span>;
    }
    if (payment.isPaid) {
      return <span className="badge bg-primary">{t("Paid")}</span>;
    }
    if (payment.isPartialPaid) {
      return <span className="badge bg-warning">{t("Partial")}</span>;
    }
    if (hasOutstanding) {
      return <span className="badge bg-danger">{t("Pending")}</span>;
    }
    if (payment.salesType === "refund") {
      return <span className="badge bg-danger">{t("Return")}</span>;
    }
  };

  const getTypeBadgeClass = (salesType) => {
    if (salesType === "sales") return "bg-secondary";
    if (salesType === "payment") return "bg-success";
    if (salesType === "refund") return "bg-danger";
    return "bg-secondary";
  };

  const renderAmountCell = (txn, amountType) => {
    const primaryValue = txn[`${amountType}Primary`];
    const secondaryValue = txn.secondaryCurrency ? txn[`${amountType}Secondary`] : 0;
    const isZero = primaryValue === 0 && (!txn.secondaryCurrency || secondaryValue === 0);
    if ((isZero && amountType === "balance") || (isZero && amountType === "total")) {
      return <td>-</td>;
    }
    const tooltip = txn.hasMoreCurrencies ? generateTooltip(txn.currencies, amountType) : null;
    // const cellProps = tooltip ? { title: tooltip } : {};
    const moreCount = txn.hasMoreCurrencies ? Object.keys(txn.currencies).length - 2 : 0;
    const isRefundClass = txn.salesType === "refund" ? "text-danger" : "";
    const primaryClass = amountType === "balance" ? (primaryValue > 0 ? "text-danger" : "text-success") : isRefundClass;
    const secondaryClass = amountType === "balance" ? (secondaryValue > 0 ? "text-danger" : "text-success") : "text-muted";
    console.log('primaryValue', primaryValue);

    return (
      // <td {...cellProps}>
      <td>
        <>
          <strong className={primaryClass}>{formatAmount(txn.primaryCurrency, primaryValue)}</strong>
          {/* {txn.secondaryCurrency && (
            <>
              <br />
              <small className={secondaryClass}>
                {formatAmount(txn.secondaryCurrency, secondaryValue)}
                {moreCount > 0 && ` +${moreCount} more`}
              </small>
            </>
          )} */}
        </>
      </td>
    );
  };

  const getCustomerType = (customerType) => {
    if (customerType === "hospital_patient") {
      return t("Clinic Patient");
    } else if (customerType === "inpatient") {
      return t("Existing");
    } else {
      return t("Walk-in");
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        mainTitle={t("Patient Ledger")}
        parent={t("Dashboard")}
        title={t("Ledger")}
      />

      <Col sm="12">
        {/* Search and Filters */}
        <Card>
          <CardBody>
            <div className="row g-2">
              <Col sm="6">
                <label className="form-label">{t("Search Patient")}</label>
                <InputGroup className="custom-input-group">
                  <Input
                    type="search"
                    className="search-bar patient-search-bar"
                    placeholder={`${isHospitalConnected ? 'Search Patient by CLINICDOTS ID / Name / Email / Phone'
                      : 'Search Patient by Name / Email / Phone'}`}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      handleSearchClick();
                    }}
                    onKeyPress={handleSearchKeyPress}
                  />
                  <InputGroup.Text
                    onClick={handleSearchClick}
                    className="text-white patient-search-icon-btn"
                    style={{
                      backgroundColor: "var(--theme-default)",
                      background:
                        "linear-gradient(180.05deg, #2B3A8F 20.14%, #1B255B 87.13%)",
                    }}
                  >
                    <i
                      className="fa fa-search"
                      style={{
                        fontSize: "14px",
                        height: "22px",
                        width: "16px",
                      }}
                    ></i>
                  </InputGroup.Text>
                </InputGroup>
              </Col>
            </div>
          </CardBody>
        </Card>

        {/* Selected Patient */}
        {selectedPatient && (
          <div className="mt-4">
            <Card
              className={`${selectedPatient?.is_death === false
                ? "patient-card-background"
                : "death-patient-card-background"
                } mb-4`}
            >
              <div className="p-2">
                <div className="p-2 d-flex tw-space-x-2">
                  <S3ImageLoader
                    fileName={selectedPatient.profile_picture}
                    alt={selectedPatient.full_name}
                    className="patient-pic img-60"
                    fallbackSrc={errorProfile}
                  />
                  <div className="ps-3">
                    <h6 className="mb-0">
                      {selectedPatient.patient_name ? (
                        <strong>
                          {selectedPatient.patient_name}
                        </strong>
                      ) : (
                        <strong>{selectedPatient.full_name}</strong>
                      )}
                    </h6>
                    {/* <small
                      style={{
                        fontSize: "12px",
                        textTransform: "capitalize",
                      }}
                    >
                      {selectedPatient.gender},{" "}
                      {moment(selectedPatient.dob).format("DD MMM YYYY")} Age{" "}
                      {calculateAge(selectedPatient.dob)}
                    </small>
                    <div className="patient-healthId-text tw-min-w-max">
                      {selectedPatient.health_id?.match(/.{1,4}/g).join(" ")}
                    </div> */}
                  </div>
                  <div className="row ps-3">
                    <div className="col-md-12">
                      <div className="d-flex align-items-center">
                        <div className="p-0 rounded-circle me-2">
                          <FiPhoneCall />
                        </div>
                        <span style={{ fontSize: "12px" }}>
                          {selectedPatient.countryCode} {selectedPatient.phone}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="d-flex align-items-center">
                        <div className="p-0 rounded-circle me-2">
                          <TfiEmail />
                        </div>
                        <span style={{ fontSize: "12px" }}>
                          {selectedPatient.email}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="d-flex align-items-center">
                        <div className="p-0 rounded-circle me-2">
                          <FaLocationDot />
                        </div>
                        <small>{selectedPatient.address}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Ledger Table */}
        <Card>
          <CardBody>
            <div className="table-responsive">
              <Table bordered hover striped>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t("Patient Name")}</th>
                    <th>{t("Type")}</th>
                    <th>{t("Transaction Type")}</th>
                    <th>{t("Total Amount")}</th>
                    <th>{t("Paid")}</th>
                    <th>{t("Currency")}</th>
                    <th>{t("Outstanding Balance")}</th>
                    <th>{t("Status")}</th>
                    <th>{t("Date")}</th>
                    <th className="text-center">{t("Action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.length > 0 ? (
                    ledger.map((txn, idx) => {
                      const hasOutstanding = Object.values(txn.currencies).some((c) => c.balance > 0);
                      const accountsTooltipId = `accounts-${txn._id}`;
                      return (
                        <tr key={txn._id} className="">
                          <td>{(page - 1) * size + idx + 1}</td>
                          <td>{txn.patientName}</td>
                          <td>{getCustomerType(txn.customerType)}</td>
                          <td>
                            <span className={`badge ${getTypeBadgeClass(txn.salesType)}`}>
                              {getTransactionTypeLabel(txn.salesType)}
                            </span>
                          </td>
                          <td>
                            {getCurrencySymbol(txn.currency)}{txn.netTotal.toFixed(2)}
                          </td>
                          <td>
                            {getCurrencySymbol(txn.currency)}{txn.amount.toFixed(2)}
                          </td>
                          <td>
                            <span className="fw-bold">
                              {txn.currency}
                            </span>
                          </td>
                          <td>
                            {getCurrencySymbol(txn.currency)}{(txn.balances[txn.currency]).toFixed(2)}
                          </td>
                          {/* {renderAmountCell(txn, "total")} */}
                          {/* {renderAmountCell(txn, "paid")} */}
                          {/* {renderAmountCell(txn, "balance")} */}
                          <td>{getStatusBadge(txn)}</td>
                          <td>{formatDate(txn.createdAt)}</td>
                          <td className="text-center">
                            {/* Settlement */}
                            {hasOutstanding && (
                              <button
                                className="success-action-button me-1"
                                id={`SettlementTooltip`}
                                onClick={() => openSettlementModal(txn)}
                              >
                                <i
                                  className="fa fa-money"
                                  style={{ fontSize: "18px" }}
                                ></i>
                                <ToolTip
                                  attrToolTip={{
                                    placement: "top",
                                    isOpen: settlementTooltipOpen,
                                    target: `SettlementTooltip`,
                                    toggle: toggleSettlementTooltip,
                                  }}
                                >
                                  <strong>{t("Pay")}</strong>
                                </ToolTip>
                              </button>
                            )}
                            {txn.patientId && (
                              <>
                                {/* View */}
                                <button
                                  className="success-action-button me-1"
                                  id={`ViewTooltip`}
                                  onClick={() =>
                                    history(`${process.env.PUBLIC_URL}/view-patient-ledger/${txn.patientId}`)
                                  }
                                >
                                  <i
                                    className="fa fa-eye"
                                    style={{ fontSize: "18px" }}
                                  ></i>
                                  <ToolTip
                                    attrToolTip={{
                                      placement: "top",
                                      isOpen: viewTooltipOpen,
                                      target: `ViewTooltip`,
                                      toggle: toggleViewTooltip,
                                    }}
                                  >
                                    <strong>{t("View")}</strong>
                                  </ToolTip>
                                </button>

                                {/* Return */}
                                <button
                                  className="success-action-button me-1"
                                  id={`ReturnTooltip`}
                                  onClick={() =>
                                    history(`${process.env.PUBLIC_URL}/return-sales`, {
                                      state: {
                                        patientId: txn.patientId,
                                        patientName: txn.patientName,
                                        stateCustomerType: txn.customerType,
                                      },
                                    })
                                  }
                                >
                                  <i
                                    className="fa fa-undo"
                                    style={{ fontSize: "18px" }}
                                  ></i>
                                  <ToolTip
                                    attrToolTip={{
                                      placement: "top",
                                      isOpen: returnTooltipOpen,
                                      target: `ReturnTooltip`,
                                      toggle: toggleReturnTooltip,
                                    }}
                                  >
                                    <strong>{t("Return")}</strong>
                                  </ToolTip>
                                </button>

                                {/* Sale */}
                                <button
                                  className="success-action-button me-1"
                                  id={`SaleTooltip`}
                                  onClick={() =>
                                    history(`${process.env.PUBLIC_URL}/new-sales`, {
                                      state: {
                                        patientId: txn.patientId,
                                        patientName: txn.patientName,
                                        stateCustomerType: txn.customerType,
                                      },
                                    })
                                  }
                                >
                                  <i
                                    className="fa fa-plus"
                                    style={{ fontSize: "18px" }}
                                  ></i>
                                  <ToolTip
                                    attrToolTip={{
                                      placement: "top",
                                      isOpen: saleTooltipOpen,
                                      target: `SaleTooltip`,
                                      toggle: toggleSaleTooltip,
                                    }}
                                  >
                                    <strong>{t("Sale")}</strong>
                                  </ToolTip>
                                </button>

                                {/* Patient Accounts */}
                                {showPatientAccounts && txn.customerType === "hospital_patient" && (
                                  <button
                                    className="success-action-button"
                                    id={accountsTooltipId}
                                    onClick={() => openPatientAccounts(txn)}
                                  >
                                    <i
                                      className="fa fa-file-text-o"
                                      style={{ fontSize: "18px" }}
                                    ></i>
                                    <ToolTip
                                      attrToolTip={{
                                        placement: "top",
                                        isOpen: actionTooltips[accountsTooltipId] || false,
                                        target: accountsTooltipId,
                                        toggle: () => toggleActionTooltip(accountsTooltipId),
                                      }}
                                    >
                                      <strong>{t("Patient Accounts")}</strong>
                                    </ToolTip>
                                  </button>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10" className="p-3 text-center text-muted">
                        {t("No transactions found")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="mt-3 d-md-flex align-items-center justify-content-between">
              <div className="show-entries">
                <Label>{t("Show")}&nbsp;</Label>
                <select
                  value={size}
                  onChange={handlePageSizeChange}
                  className="form-control"
                  style={{ display: "inline", width: "auto" }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                &nbsp;<Label>{t("entries")}</Label>
              </div>
              <MyPagination
                totalPages={totalItems}
                currentPage={page}
                onPageChange={handlePageChange}
              />
            </div>
          </CardBody>
        </Card>
      </Col>

      {/* Settlement Modal */}
      <Modal
        show={showSettlementModal}
        onHide={() => setShowSettlementModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Add Payment")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* <Form.Group className="mb-3">
              <Form.Label>{t("Currency")}</Form.Label>
              <Row>
                <Col sm="6" className="mb-2">
                  <div className="radio radio-primary">
                    <Input
                      id="currency-primary"
                      type="radio"
                      name="currency"
                      value={settlementData.primaryCurrency}
                      checked={currency === settlementData.primaryCurrency}
                      onChange={() => handleCurrencyChange(settlementData.primaryCurrency)}
                    />
                    <Label htmlFor="currency-primary" className="ps-3">
                      {settlementData.primaryCurrency}
                      <span className="text-muted ms-2">
                        ({formatAmount(settlementData.primaryCurrency, settlementData.remainingPrimary)})
                      </span>
                    </Label>
                  </div>
                </Col>
                {settlementData.secondaryCurrency && (
                  <Col sm="6" className="mb-2">
                    <div className="radio radio-primary">
                      <Input
                        id="currency-secondary"
                        type="radio"
                        name="currency"
                        value={settlementData.secondaryCurrency}
                        checked={currency === settlementData.secondaryCurrency}
                        onChange={() => handleCurrencyChange(settlementData.secondaryCurrency)}
                      />
                      <Label htmlFor="currency-secondary" className="ps-3">
                        {settlementData.secondaryCurrency}
                        <span className="text-muted ms-2">
                          ({formatAmount(settlementData.secondaryCurrency, settlementData.remainingSecondary)})
                        </span>
                      </Label>
                    </div>
                  </Col>
                )}
              </Row>
            </Form.Group> */}

            <Form.Group className="mb-3">
              <Form.Label>{t("Payment Type")}</Form.Label>
              <Form.Select
                value={settlementData.paymentType}
                onChange={(e) =>
                  setSettlementData({
                    ...settlementData,
                    paymentType: e.target.value,
                  })
                }
                isInvalid={!!errors.paymentType}
              >
                <option value="cash">{t("Cash")}</option>
                <option value="card">{t("Card")}</option>
                <option value="upi">{t("UPI")}</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.paymentType}
              </Form.Control.Feedback>
            </Form.Group>
            <div className="mb-3 p-2 rounded">
              <strong>{t("Outstanding Balances")}:</strong>

              {Object.entries(settlementData.rates).map(([cur]) => (
                <div key={cur} className="d-flex justify-content-between mt-1">
                  <span>{cur}</span>
                  <span className="fw-bold">
                    {formatAmount(cur, settlementData.remainingAmounts?.[cur] || settlementData.balances?.[cur])}
                  </span>
                </div>
              ))}
            </div>

            {/* <Form.Group className="mb-3">
              <Form.Label>{t("Amount")}</Form.Label>
              <Form.Control
                type="number"
                value={settlementData.amount}
                onChange={(e) =>
                  setSettlementData({
                    ...settlementData,
                    amount: e.target.value,
                  })
                }
                {...disableScrollProps}
                isInvalid={!!errors.amount}
              />
              <Form.Control.Feedback type="invalid">
                {errors.amount}
              </Form.Control.Feedback>
            </Form.Group> */}
            <CurrencyAmountInput
              label={t("Amount")}
              amount={settlementData.amount}
              onAmountChange={(value) => setSettlementData(prev => ({
                ...prev,
                amount: value
              }))
              }
              onCurrencyChange={(cur) => setSettlementData(prev => ({
                ...prev,
                currency: cur
              }))
              }
              disabled={false}
              errors={errors}
              disableScrollProps={disableScrollProps}
              defaultCurrency={settlementData.baseCurrency}
              originalAmount={settlementData.remainingAmounts?.[settlementData.baseCurrency] || 0}
              paidInCurrency={settlementData.currency}
              settlementData={settlementData}
            />
            <div className="p-2 my-3 d-flex justify-content-end flex-column payment-info bg-primary-subtle">
              <div className="mb-1">
                <span className="ms-3 text-danger">
                  {t("Remaining")}: {getCurrencySymbol(settlementData.currency)}
                  {(() => {
                    const cur = settlementData.currency;

                    const totalBalance = settlementData.remainingAmounts?.[cur] ?? 0;
                    const paidAmount = Number(settlementData.amount || 0);

                    const remaining = Math.max(0, totalBalance - paidAmount);

                    return remaining.toFixed(2);
                  })()}
                </span>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>{t("Settlement")}</Form.Label>
              <Row>
                <Col sm="6" className="mb-2">
                  <div
                    className="checkbox checkbox-solid-light"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Input
                      id={`isSettle-checkbox`}
                      type="checkbox"
                      checked={settlementData.isSettle}
                      className="me-2"
                      onChange={handleSettlePayment}
                    />
                    <label
                      htmlFor={`isSettle-checkbox`}
                      style={{ fontSize: "14px", margin: 0 }}
                    >
                      <strong>{t("Payment Settlement")}</strong>
                    </label>
                  </div>
                </Col>
              </Row>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button color="primary" onClick={handleSettlementSave}>
            {t("Save")}
          </Button>
          <Button
            color="secondary"
            onClick={() => setShowSettlementModal(false)}
          >
            {t("Cancel")}
          </Button>
        </Modal.Footer>
      </Modal>

      <SearchPatientModel
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isPatientModal={isPatientModal}
        setIsPatientModal={setIsPatientModal}
        handleSelectPatient={(patient) => {
          setSelectedPatient(patient);
          setIsPatientModal(false);
          handleSearch(patient);
        }}
      />
    </Fragment>
  );
};

export default PatientLedger;